import { CognitoUser } from '@aws-amplify/auth'
import { message } from 'antd'
import { Auth } from 'aws-amplify'
import axios, { AxiosError, AxiosRequestConfig } from 'axios'

export const axiosInstance = axios.create()

let isRefreshing = false
type FailedQueueItem = {
  resolve: (config: AxiosRequestConfig) => void
  reject: (error: unknown) => void
}
let failedQueue: FailedQueueItem[] = []

const processQueue = (error: unknown = null, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else if (token && resolve) {
      resolve({ headers: { Authorization: `Bearer ${token}` } })
    }
  })
  failedQueue = []
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise<unknown>((resolve, reject) => {
          failedQueue.push({
            resolve: (config) => {
              if (config?.headers) {
                originalRequest.headers = { ...originalRequest.headers, ...config.headers }
              }
              originalRequest._retry = true
              resolve(axiosInstance(originalRequest))
            },
            reject
          })
        })
      }

      isRefreshing = true
      originalRequest._retry = true

      try {
        const user = await Auth.currentAuthenticatedUser() as CognitoUser
        const refreshToken = user?.getSignInUserSession()?.getRefreshToken()

        if (refreshToken) {
          message.loading({ content: 'Login session expired. Refreshing token...', key: 'refresh' })

          const newToken = await new Promise<string>((resolve, reject) => {
            user.refreshSession(refreshToken, (err, result) => {
              if (err || !result?.idToken) {
                reject(err || new Error('No id token'))
              } else {
                resolve(result.idToken.jwtToken)
              }
            })
          })
          localStorage.setItem('token', newToken)
          message.success('Refresh token successfully!')
          isRefreshing = false

          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          processQueue(null, newToken)

          return axiosInstance(originalRequest)
        } else {
          message.warning('Login session expired, please login again!')
          localStorage.removeItem('token')
          isRefreshing = false
          processQueue(error, null)
          window.location.href = '/signin'
          return Promise.reject(error)
        }
      } catch (e) {
        const failureError = e ?? error
        message.warning('Login session expired, please login again!')
        localStorage.removeItem('token')
        isRefreshing = false
        processQueue(failureError, null)
        window.location.href = '/signin'
        return Promise.reject(failureError)
      } finally {
        message.destroy('refresh')
      }
    }
    return Promise.reject(error)
  }
)
